import { merge } from 'lodash';
import type { TypePolicies as ApolloTypePolicies } from '@apollo/client';
import { typePolicies as AccountNotificationSettingsTypePolicies } from '../client-schema/Account.typePolicy';
import { typePolicies as GetAnnotationStateTypePolicies } from '../client-schema/annotations/Query.getAnnotationState.typePolicy';
import { typePolicies as CommentTypePolicies } from '../client-schema/Comment.typePolicy';
import { typePolicies as GetMediaStateTypePolicies } from '../client-schema/media-state/Query.getMediaState.typePolicy';
import { typePolicies as NotificationGroupConnectionTypePolicies } from '../client-schema/NotificationGroupConnection.typePolicy';
import { typePolicies as GetTransferBatchesForAccountTypePolicies } from '../client-schema/transfers/Query.GetTransferBatchesForAccount.typePolicy';
import { typePolicies as GetUploaderConfigTypePolicies } from '../client-schema/transfers/Query.GetUploaderConfig.typePolicy';
import { typePolicies as GetUploaderConfigsTypePolicies } from '../client-schema/transfers/Query.GetUploaderConfigs.typePolicy';
import { typePolicies as AccountUserGroupsPolicies } from './features/account-settings/account-user-groups/AccountUserGroups.typePolicy';
import { typePolicies as GetSelectedAccountUserGroupsTypePolicies } from './features/account-settings/account-user-groups/queries/GetSelectedAccountUserGroups.typePolicy';
import { typePolicies as AccountUserTypePolicies } from './features/account-settings/account-users/queries/AccountUser.typePolicy';
import { typePolicies as AccountUserDirectMembershipsTypePolicies } from './features/account-settings/account-users/queries/AccountUserDirectMemberships.typePolicy';
import { typePolicies as GetAccountUsersTypePolicies } from './features/account-settings/account-users/queries/GetAccountUsers.typePolicy';
import { typePolicies as GetSelectedAccountUsersTypePolicies } from './features/account-settings/account-users/queries/GetSelectedAccountUsers.typePolicy';
import { typePolicies as ResourceControlPoliciesPolicy } from './features/account-settings/content-security/queries/ResourceControlPolicies.typePolicy';
import { typePolicies as GetAccountProjectsTypePolicies } from './features/account-settings/projects/queries/GetAccountProjects.typePolicy';
import { typePolicies as GetSelectedProjectsTypePolicies } from './features/account-settings/projects/queries/GetSelectedProjects.typePolicy';
import { typePolicies as ShareListTypePolicies } from './features/account-settings/share-lists/ShareList.typePolicy';
import { typePolicies as ImagesTypePolicies } from './features/account-settings/user-profile/queries/Images.typePolicy';
import { typePolicies as GetSelectedActionsTypePolicies } from './features/actions/queries/GetSelectedActions.typePolicy';
import { typePolicies as AfterEffectsTypePolicies } from './features/aftereffects/AfterEffects.typePolicy';
import { typePolicies as AgentChatTypePolicies } from './features/ai/AgentChat.typePolicy';
import { typePolicies as VersionTimelineTypePolicies } from './features/assets/queries/VersionTimeline.typePolicy';
import { typePolicies as AssetScopeSubscriptionTypePolicies } from './features/assets/subscriptions/AssetScopeSubscription.typePolicy';
import { typePolicies as GetAssetDetailsPanelStateTypePolicies } from './features/assets-panel/assets-details-panel/queries/GetAssetDetailsPanelState.typePolicy';
import { typePolicies as ProjectFieldDefinitionTypePolicies } from './features/field-definition/types/ProjectFieldDefinition.typePolicy';
import { typePolicies as AssetTypePolicies } from './features/finder-performance/Asset.typePolicy';
import { typePolicies as CollectionTypePolicies } from './features/finder-performance/Collection.typePolicy';
import { typePolicies as CollectionAssetTypePolicy } from './features/finder-performance/CollectionAsset.typePolicy';
import { typePolicies as CollectionAssetsTypePolicies } from './features/finder-performance/collectionAssets.typePolicy';
import { typePolicies as FieldValuesTypePolicies } from './features/finder-performance/FieldValues.typePolicy';
import { typePolicies as FolderAssetsTypePolicies } from './features/finder-performance/folderAssets.typePolicy';
import { typePolicies as MetadataFilterTypePolicies } from './features/finder-performance/MetadataFilter.typePolicy';
import { typePolicies as TrashAssetsTypePolicies } from './features/finder-performance/trashAssets.typePolicy';
import { typePolicies as GetEphemeralFiltersTypePolicies } from './features/in-project-search/queries/GetEphemeralFilters.typePolicy';
import { typePolicies as GetRecentInProjectSearchesTypePolicies } from './features/in-project-search/queries/GetRecentInProjectSearches.typePolicy';
import { typePolicies as GetUxpLocalStorageItemTypePolicies } from './features/integrations/queries/GetUxpLocalStorateItem.typePolicy';
import { typePolicies as GetSuggestedMembersTypePolicies } from './features/invites/queries/GetSuggestedMembers.typePolicy';
import { typePolicies as ResourceMembershipsTypePolicies } from './features/invites/ResourceMemberships.typePolicy';
import { typePolicies as GetFinderItemsTypePolicies } from './features/legacy-finder/GetFinderItems.typePolicy';
import { typePolicies as GetProjectForHeaderTypePolicies } from './features/navigation/queries/GetProjectForHeader.typePolicy';
import { typePolicies as PermissionsTypePolicies } from './features/permissions/types/permissions.typePolicy';
import { typePolicies as PremiereTypePolicies } from './features/premiere/Premiere.typePolicy';
import { typePolicies as ProjectFeatureTypePolicies } from './features/projects/Project.typePolicy';
import { typePolicies as QuickSearchTypePolicies } from './features/quick-search/QuickSearch.typePolicy';
import { typePolicies as keyFieldsTypePolicies } from './features/shares/keyFields.typePolicy';
import { typePolicies as GetShareActivitiesTypePolicies } from './features/shares/queries/GetShareActivities.typePolicy';
import { typePolicies as GetShareAssetsTypePolicies } from './features/shares/queries/GetShareAssets.typePolicy';
import { typePolicies as GetShareReviewersTypePolicies } from './features/shares/queries/GetShareReviewers.typePolicy';
import { typePolicies as AssetAggregateAppearanceHeaderTypePolicies } from './features/shares/types/AssetAggregateAppearanceHeader.typePolicy';
import { typePolicies as CollectionShareTypePolicies } from './features/shares/types/CollectionShare.typePolicy';
import { typePolicies as ShareFieldTypePolicies } from './features/shares/types/ShareField.typePolicy';
import { typePolicies as GetSyncSessionTypePolicies } from './features/sync-session/queries/GetSyncSession.typePolicy';
import { typePolicies as GetSyncSessionsTypePolicies } from './features/sync-session/queries/GetSyncSessions.typePolicy';
import { typePolicies as GetUndoRedoCommandTypePolicies } from './features/undo-redo-demo/queries/GetUndoRedoCommand.typePolicy';
import { typePolicies as GetUserPrefsTypePolicies } from './features/user-prefs/queries/GetUserPrefs.typePolicy';
import { typePolicies as GetSelectedWebhooksTypePolicies } from './features/webhooks/queries/GetSelectedWebhooks.typePolicy';
import { typePolicies as assetTypePolicies } from './shared/queries/asset.typePolicy';
import { typePolicies as AvailableAdobeImsGroupsWithFilterTypePolicies } from './shared/queries/availableAdobeImsGroupsWithFilter.typePolicy';
import { typePolicies as GetUnifiedApprovalAssetApprovalsTypePolicies } from './shared/queries/GetUnifiedApprovalAssetApprovals.typePolicy';
import { typePolicies as AccountTypePolicies } from './shared/types/Account.typePolicy';
import { typePolicies as AccountAppIntegrationsTypePolicies } from './shared/types/AccountAppIntegrations.typePolicy';
import { typePolicies as AccountSubscriptionTypePolicies } from './shared/types/AccountSubscription.typePolicy';
import { typePolicies as AssetsTypePolicies } from './shared/types/Assets.typePolicy';
import { typePolicies as AuthenticatedUserTypePolicies } from './shared/types/AuthenticatedUser.typePolicy';
import { typePolicies as InvoiceTypePolicies } from './shared/types/Invoice.typePolicy';
import { typePolicies as PlanTypePolicies } from './shared/types/Plan.typePolicy';
import { typePolicies as ProjectTypePolicies } from './shared/types/Project.typePolicy';
import { typePolicies as RoleTypePolicies } from './shared/types/Role.typePolicy';
import { typePolicies as SeatUsageByLicenseTypeMappingTypePolicies } from './shared/types/SeatUsageByLicenseTypeMapping.typePolicy';
import { typePolicies as UserInboxTypePolicies } from './shared/types/UserInbox.typePolicy';
import { typePolicies as WorkspaceTypePolicies } from './shared/types/Workspace.typePolicy';

export const typePolicies = merge(
  AccountSubscriptionTypePolicies,
  AfterEffectsTypePolicies,
  AccountTypePolicies,
  AccountAppIntegrationsTypePolicies,
  AgentChatTypePolicies,
  AccountNotificationSettingsTypePolicies,
  AccountUserDirectMembershipsTypePolicies,
  AccountUserTypePolicies,
  AccountUserGroupsPolicies,
  AssetScopeSubscriptionTypePolicies,
  AssetAggregateAppearanceHeaderTypePolicies,
  AssetsTypePolicies,
  assetTypePolicies,
  AssetTypePolicies,
  AuthenticatedUserTypePolicies,
  CollectionAssetsTypePolicies,
  CollectionShareTypePolicies,
  CollectionTypePolicies,
  CollectionAssetTypePolicy,
  CommentTypePolicies,
  FieldValuesTypePolicies,
  FolderAssetsTypePolicies,
  GetAccountProjectsTypePolicies,
  GetAccountUsersTypePolicies,
  GetAnnotationStateTypePolicies,
  GetAssetDetailsPanelStateTypePolicies,
  AvailableAdobeImsGroupsWithFilterTypePolicies,
  GetEphemeralFiltersTypePolicies,
  GetFinderItemsTypePolicies,
  GetMediaStateTypePolicies,
  GetProjectForHeaderTypePolicies,
  GetRecentInProjectSearchesTypePolicies,
  GetSelectedAccountUserGroupsTypePolicies,
  GetSelectedAccountUsersTypePolicies,
  GetSelectedActionsTypePolicies,
  GetSelectedProjectsTypePolicies,
  GetShareActivitiesTypePolicies,
  GetShareAssetsTypePolicies,
  GetShareReviewersTypePolicies,
  GetSuggestedMembersTypePolicies,
  ResourceMembershipsTypePolicies,
  GetSyncSessionsTypePolicies,
  GetSyncSessionTypePolicies,
  GetTransferBatchesForAccountTypePolicies,
  GetUndoRedoCommandTypePolicies,
  GetUploaderConfigsTypePolicies,
  GetUploaderConfigTypePolicies,
  GetUserPrefsTypePolicies,
  GetSelectedWebhooksTypePolicies,
  GetUxpLocalStorageItemTypePolicies,
  ImagesTypePolicies,
  InvoiceTypePolicies,
  keyFieldsTypePolicies,
  MetadataFilterTypePolicies,
  NotificationGroupConnectionTypePolicies,
  PermissionsTypePolicies,
  PlanTypePolicies,
  ProjectFeatureTypePolicies,
  PremiereTypePolicies,
  ProjectFieldDefinitionTypePolicies,
  ProjectTypePolicies,
  SeatUsageByLicenseTypeMappingTypePolicies,
  ShareFieldTypePolicies,
  ShareListTypePolicies,
  TrashAssetsTypePolicies,
  WorkspaceTypePolicies,
  ResourceControlPoliciesPolicy,
  RoleTypePolicies,
  QuickSearchTypePolicies,
  UserInboxTypePolicies,
  GetUnifiedApprovalAssetApprovalsTypePolicies,
  VersionTimelineTypePolicies
) as ApolloTypePolicies;
